import type { ComboBoxFilterChangeEvent } from "@tql/ui-components-react/kendo/dropdowns";
import { Card } from "@tql/ui-components-react/kendo/layout";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { DatePicker } from "@tql/ui-components-react/tql/dateinputs";
import { ComboBox } from "@tql/ui-components-react/tql/dropdowns";
import { FormField } from "@tql/ui-components-react/tql/form/beta";
import {
	copy_02,
	dotsGrid,
	trash_01,
} from "@tql/ui-components-react/untitled/icons";
import { useCallback, useContext } from "react";
import { useDebounceCallback } from "usehooks-ts";

import type { StopSortableProps } from "~/features/ftl/ftl-quotes/create-quote/components/quote-details-step/quote-details-step.tsx";
import { sortableItemUIContext } from "~/features/ftl/ftl-quotes/create-quote/components/quote-details-step/quote-details-step.tsx";
import { useMinStopDate } from "~/features/ftl/ftl-quotes/hooks/use-after-2pm-est.tsx";
import { useLocationSearch } from "~/features/ftl/ftl-quotes/hooks/use-location-search.tsx";
import type { Stop } from "~/features/ftl/shared/ftl-stop.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";

import { StopTypeSelector } from "../../../stop-type-selector/stop-type-selector.tsx";
import styles from "./ftl-stop.module.css";

export type StopProps = {
	stop: Stop;
};

function validateMinDate(selectedDate: Date, minDate: Date): string {
	if (selectedDate < minDate) {
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		if (selectedDate.getTime() === today.getTime()) {
			return "After 2 PM EST, same-day stop dates are not available. Please select a future date.";
		}

		return "Stop Date cannot be in the past. Please select a valid date.";
	}
	return "";
}

export function validateStopDateAgainstPreviousAndNext(
	value: Date | null,
	minDate: Date,
	previousStopDate: Date | null,
	nextStopDate: Date | null,
	currentIndex: number,
) {
	if (!value) {
		return "Stop Date is required.";
	}

	const selectedDate = new Date(value);
	selectedDate.setHours(0, 0, 0, 0);

	const minDateNormalized = new Date(minDate);
	minDateNormalized.setHours(0, 0, 0, 0);

	const minDateError = validateMinDate(selectedDate, minDateNormalized);
	if (minDateError) return minDateError;

	if (previousStopDate) {
		const previousDate = new Date(previousStopDate);
		previousDate.setHours(0, 0, 0, 0);

		if (selectedDate < previousDate) {
			return `Stop date must be on or after Stop ${currentIndex} date (${previousDate.toLocaleDateString()}).`;
		}
	}

	if (nextStopDate) {
		const nextDate = new Date(nextStopDate);
		nextDate.setHours(0, 0, 0, 0);

		if (selectedDate > nextDate) {
			return `Stop date must be on or before Stop ${currentIndex + 2} date (${nextDate.toLocaleDateString()}).`;
		}
	}

	return "";
}

const locationValidator = (value: string) =>
	value ? "" : "Location is required.";

const stopTypeValidator = (value: string) =>
	value ? "" : "Stop Type is required.";

export function FtlStop(props: Readonly<StopProps>) {
	const MOBILE_BREAKPOINT = 601;
	const isMobile = useIsMobile(MOBILE_BREAKPOINT);

	const stopsContext = useContext<StopSortableProps>(sortableItemUIContext);
	const minStopDate = useMinStopDate();

	const stops = stopsContext?.stops ?? [];
	const deletable = stops.length > 2;

	const onDeleteStop = (index: number) => {
		stopsContext?.onRemove(index);
	};

	const onCopyStop = (index: number) => {
		const selectedLocation = props.stop.location;
		const currentOptions = locationsData ?? [];
		const alreadyIncluded = currentOptions.some(
			(l) => l.location === selectedLocation?.location,
		);
		const locationOptions =
			selectedLocation && !alreadyIncluded
				? [selectedLocation, ...currentOptions]
				: locationsData;
		stopsContext?.onCopy(index, locationOptions);
	};

	const stopIndex = stops.findIndex((stop) => stop.id === props.stop.id);
	const name = `stops[${stopIndex}]`;
	const { search, isPending, locationsData } = useLocationSearch(
		props.stop.locationOptions,
	);

	const locationSearch = useDebounceCallback(search, 300);

	const handleFilterChange = useCallback(
		(event: ComboBoxFilterChangeEvent) => {
			if ((event.filter.value as string).length > 2) {
				locationSearch(event.filter.value as string);
			}
		},
		[locationSearch],
	);

	const previousStopDate = stops[stopIndex - 1]?.stopDate ?? null;
	const nextStopDate = stops[stopIndex + 1]?.stopDate ?? null;
	const stopDateFieldValidator = useCallback(
		(value: Date | null) =>
			validateStopDateAgainstPreviousAndNext(
				value,
				minStopDate,
				previousStopDate,
				nextStopDate,
				stopIndex,
			),
		[minStopDate, previousStopDate, nextStopDate, stopIndex],
	);

	return (
		<Card>
			<div
				className={styles.stopContainer}
				data-testid={`ftl-stop-${stopIndex}`}
			>
				<div className={styles.stopControls}>
					<div className={styles.stopTypeControl}>
						<FormField
							required
							className={"k-mt-0"}
							component={StopTypeSelector}
							deletable={deletable}
							label={"Stop Type"}
							name={`${name}.stopType`}
							onDeleteStop={onDeleteStop}
							stop={props.stop}
							stopIndex={stopIndex}
							validator={stopTypeValidator}
						/>
					</div>
					<div
						className={styles.stopDateControl}
						data-testid={`ftl-stop-date-${stopIndex}`}
					>
						<FormField
							required
							component={DatePicker}
							defaultValue={minStopDate}
							label={"Stop Date"}
							min={minStopDate}
							name={`${name}.stopDate`}
							validator={stopDateFieldValidator}
						/>
					</div>
					<div
						className={styles.stopLocationControl}
						data-testid={`stop-location-${stopIndex}`}
					>
						<FormField
							filterable
							required
							component={ComboBox}
							data={locationsData ?? undefined}
							dataItemKey={"location"}
							label={"City, State/Province or Postal Code"}
							loading={isPending}
							name={`${name}.location`}
							onFilterChange={handleFilterChange}
							placeholder={"Search by city name or postal code."}
							textField={"location"}
							validator={locationValidator}
						/>
					</div>
					<div
						className={"k-d-flex k-grid-cols-3 k-gap-2"}
						style={
							isMobile
								? { alignSelf: "flex-end" }
								: { paddingTop: "var(--kendo-spacing-7)" }
						}
					>
						<div
							className={"k-flex-basis-1/3"}
							style={
								isMobile
									? { display: "flex", justifyContent: "flex-start" }
									: undefined
							}
						>
							<div
								className={stopIndex === 0 ? styles.hiddenButton : undefined}
							>
								<Button
									data-testid={`stop-delete-${stopIndex}`}
									formAction={"none"}
									onClick={() => onDeleteStop(stopIndex)}
									svgIcon={trash_01}
									title={"Delete stop"}
									variant={"dangerTertiary"}
								/>
							</div>
						</div>
						<div className={"k-flex-basis-1/3"}>
							<Button
								data-testid={`stop-copy-${stopIndex}`}
								formAction={"none"}
								onClick={() => onCopyStop(stopIndex)}
								svgIcon={copy_02}
								title={"Duplicate stop"}
								variant={"tertiary"}
							/>
						</div>
						<div className={"k-flex-basis-1/3"}>
							<Button
								data-testid={`stop-drag-${stopIndex}`}
								formAction={"none"}
								svgIcon={dotsGrid}
								title={"Drag to reorder"}
								variant={"tertiary"}
							/>
						</div>
					</div>
				</div>
			</div>
		</Card>
	);
}
